import { PencilIcon } from "@heroicons/react/24/outline";
import ContextButton from "@via/components/context-button/context-button";
import { GenericError } from "@via/components/error.tsx";
import { LogoSpinner, TitleLoader } from "@via/components/loading/loading.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { HorizontalTabs } from "@via/components/tabs.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import {
  usePipelineDetails,
  usePipelineRevisionHistory,
  useDownloadViaPipeline,
  useDownloadPipeline,
} from "@via/pages/projects/pipelines/pipelines.api.tsx";
import PipelineLintPanel from "@via/pages/projects/pipelines/pipeline-lint-panel";
import DeletePipelineModal from "@via/pages/projects/pipelines/workflow/components/delete-pipeline-modal.tsx";
import RevisePipelineModal from "@via/pages/projects/pipelines/workflow/components/revise-pipeline-modal.tsx";
import SavePipelineModal from "@via/pages/projects/pipelines/workflow/components/save-pipeline-modal.tsx";
import DuplicatePipelineModal from "@via/pages/projects/pipelines/workflow/components/duplicate-pipeline-modal.tsx";
import ImportPipelineModal from "@via/pages/projects/pipelines/workflow/components/import-pipeline-modal.tsx";
import ExportPipelineModal from "@via/pages/projects/pipelines/workflow/components/export-pipeline-modal.tsx";

import {
  checkDuplicateLabels,
  cleanAndParsePipeline,
  findHighestGNum,
  formatPipelineDraft,
} from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { useProject } from "@via/pages/projects/projects.api.tsx";
import CreateRunModal from "@via/pages/projects/runs/create-run-modal.tsx";
import ExistingRunsModal from "@via/pages/projects/runs/existing-runs-modal.tsx";
import { useParameterList } from "@via/pages/projects/runs/runs.api.tsx";
import {
  getMainPipeline,
  isPipelineExternal,
} from "@via/pages/projects/runs/utils/helpers.tsx";
import {
  BUTTONS,
  ERROR_YOU_ARE_NOT_AUTHORIZED_TO_ACCESS_THIS_PIPELINE,
  PIPELINE_TABS,
  USER_ROLES,
} from "@via/utils/constants.tsx";
import {
  classNames,
  formatDateString,
  isNotEqualArray,
} from "@via/utils/helpers.tsx";
import {
  SelectOption,
  TableContextMenuInterface,
} from "@via/utils/interfaces.tsx";
import {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { SocketProvider } from "@via/context/socket-context";
import "./pipeline.css";
import { SaveIcon } from "@via/utils/custom-icons";
import { useNavigationBlocker } from "@via/hooks/useBeforeUnload";
import { useChangesDetect } from "@via/hooks/useChangesDetect";
import TableContextMenu from "@via/components/table/table-context-menu";
import { createMultiConfig } from "@via/components/tree-structure/helpers/utils";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import { Badge } from "@via/components/badge";

const WORKFLOW = "Workflow";
const GIT = "Git";
const PROVENANCE = "Provenance";

const NEW_PIPELINE = "New Pipeline";

export const SOCKET_URI = "/ai";

interface PipelineRevisionOption extends SelectOption {
  revUUID: string;
  createdAt: string;
  revComment: string;
}

export default function Pipeline(): JSX.Element {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const measureRef = useRef<HTMLSpanElement>(null);
  const pipelineNameInputRef = useRef<HTMLInputElement>(null);
  const [pipelineNameInputWidth, setPipelineNameInputWidth] =
    useState<number>(100);
  const [selectedRevision, setSelectedRevision] =
    useState<PipelineRevisionOption | null>(null);
  const [pipelineRevisions, setPipelineRevisions] = useState<
    PipelineRevisionOption[]
  >([]);
  const { pipelineId = "", projectId = "" } = useParams();
  const [pipelineName, setPipelineName] = useState<string>(NEW_PIPELINE);
  const [isEditingPipelineName, setIsEditingPipelineName] = useState(false);
  const [hidden, setHidden] = useState<string[] | []>([]);
  const [isCreateRunModalOpen, setIsCreateRunModalOpen] = useState(false);
  const [isSavePipelineModalOpen, setIsSavePipelineModalOpen] = useState(false);
  const [isRevisePipelineModalOpen, setIsRevisePipelineModalOpen] =
    useState(false);
  const [isDuplicatePipelineModalOpen, setIsDuplicatePipelineModalOpen] =
    useState(false);
  const [isDeletePipelineModalOpen, setIsDeletePipelineModalOpen] =
    useState(false);
  const [isImportPipelineModalOpen, setIsImportPipelineModalOpen] =
    useState(false);
  const [isExportPipelineModalOpen, setIsExportPipelineModalOpen] =
    useState(false);
  const [isExistingRunsModalOpen, setIsExistingRunsModalOpen] = useState(false);
  const [duplicatedLabels, setDuplicatedLabels] = useState<
    Record<string, string[]>
  >({});
  const isDuplicatedLabelExists =
    duplicatedLabels?.moduleNode?.length > 0 ||
    duplicatedLabels?.modelNode?.length > 0 ||
    duplicatedLabels?.processNode?.length > 0;

  const {
    setGNum,
    setSelectedPipeline,
    setParameters,
    scriptPipeConfig,
    setScriptPipeConfig,
    mainPipeline,
    setMainPipeline,
    setSelectedProject,
    pipelineDraft,
    setPipelineDraft,
  } = useGlobal();
  const { changeCount, increaseChangeCount, resetChangeCount } =
    useChangesDetect();
  const {
    data: pipelineData,
    isLoading: isPipelineDataLoading,
    isError,
    isSuccess: isPipelineDataSuccess,
  } = usePipelineDetails(pipelineId);
  const { data: parameterData, isSuccess: isParameterDataSuccess } =
    useParameterList();
  const { data: projectData, isSuccess: isProjectDataSuccess } =
    useProject(projectId);

  const {
    data: pipelineRevisionHistoryData,
    isLoading: isPipelineRevisionHistoryLoading,
    isSuccess: isPipelineRevisionHistorySuccess,
  } = usePipelineRevisionHistory(pipelineId);

  const { navigate, NavigationBlockerModal } = useNavigationBlocker(
    pipelineDraft?.isEditingAllowed ? changeCount > 0 : false,
    [
      `/pipelines/${pipelineId}/description`,
      `/pipelines/${pipelineId}/workflow`,
      `/pipelines/${pipelineId}/advanced`,
      `/pipelines/${pipelineId}/settings`,
      `/pipelines/${pipelineId}/git`,
      `/pipelines/${pipelineId}/provenance`,
    ]
  );

  const {
    data: resp,
    mutate: downloadViaPipeline,
    isPending: isExportingFoundry,
    isSuccess: isExportSuccess,
  } = useDownloadViaPipeline();

  const {
    data: downloadResp,
    mutate: downloadPipeline,
    isSuccess: isDownloadSuccess,
  } = useDownloadPipeline();

  useEffect(() => {
    if (!isExportSuccess || !resp) return;
    const blob = resp.data;
    const fname =
      resp.headers["x-download-filename"] ||
      `${(pipelineDraft?.name || "pipeline")
        .replace(/[^\w\d\-_. ]+/g, "_")
        .trim()}.vf`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [isExportSuccess, resp]);

  useEffect(() => {
    if (!isDownloadSuccess || !downloadResp) return;
    const blob = downloadResp.data;
    const fname =
      downloadResp.headers["x-download-filename"] ||
      `${(pipelineDraft?.name || "pipeline")
        .replace(/[^\w\d\-_. ]+/g, "_")
        .trim()}.zip`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [isDownloadSuccess, downloadResp]);

  useEffect(() => {
    // Reset state when pipelineId changes
    resetPipelineDatum();

    // Guard clause to ensure required data is available
    if (!pipelineData) return;

    // Extract and set main pipeline information
    const main = getMainPipeline(pipelineData?.pipeline);
    const result = cleanAndParsePipeline(main);

    // We need to hide "Workflow" tab for nf-core pipelines since they are not compatible with the visualization
    isPipelineExternal(result?.pipeline_type) &&
      setHidden([WORKFLOW, GIT, PROVENANCE]);
    const gNum = findHighestGNum(result?.nodes) ?? 0;
    setGNum(gNum);
    setMainPipeline(result);

    setPipelineName(result.name ?? NEW_PIPELINE);

    setSelectedPipeline(pipelineData?.pipeline);

    const formatted = formatPipelineDraft(
      result,
      user?.userRole,
      pipelineData?.updateAllowed
    );
    setPipelineDraft(prevData => ({ ...prevData, ...formatted }));

    // Reset script configuration if the pipeline selection has changed
    // For external pipelines (nf-core/nextflow), always use fresh data from API
    // to ensure import updates are reflected
    const isExternal = isPipelineExternal(result?.pipeline_type);
    if (isNotEqualArray(main, mainPipeline) || isExternal) {
      const editorScriptPipeConfig = createMultiConfig(formatted);
      setScriptPipeConfig(editorScriptPipeConfig);
    }
  }, [pipelineId, pipelineData, isPipelineDataSuccess]);

  useEffect(() => {
    if (pipelineRevisionHistoryData && pipelineDraft) {
      const options =
        pipelineRevisionHistoryData?.data?.map(rev => {
          return {
            label: rev?.version?.length
              ? `Version: ${rev.version}`
              : "Version: 1.0.0",
            version: rev?.version?.length ? rev.version : "1.0.0",
            createdAt: rev?.createdAt,
            revComment: rev?.revComment?.length
              ? rev?.revComment
              : "Initial Version",
            value: rev?.id,
            revUUID: rev?.revUUID,
          };
        }) || [];
      let selected = options?.find(
        (rev: SelectOption) => rev?.value === pipelineDraft?.id
      );
      if (!selected) {
        selected = options?.[0] ?? null;
      }
      setSelectedRevision(selected);
      setPipelineRevisions(options);
    }
  }, [
    isPipelineRevisionHistorySuccess,
    pipelineRevisionHistoryData,
    pipelineDraft,
    pipelineId,
  ]);

  useEffect(() => {
    return () => {
      resetPipelineDatum();
    };
  }, []);

  useEffect(() => {
    if (pipelineDraft) {
      const duplicatedLabels = checkDuplicateLabels(pipelineDraft?.nodes ?? {});
      setDuplicatedLabels(duplicatedLabels);
    }
  }, [pipelineDraft]);

  const handleSavePipeline = useCallback(() => {
    if (pipelineDraft) {
      setIsSavePipelineModalOpen(true);
    }
  }, [pipelineDraft, scriptPipeConfig]);

  const handleRevisePipeline = useCallback(() => {
    if (pipelineDraft) {
      setIsRevisePipelineModalOpen(true);
    }
  }, [pipelineDraft, scriptPipeConfig]);

  const handleDuplicatePipeline = useCallback(() => {
    if (pipelineDraft) {
      setIsDuplicatePipelineModalOpen(true);
    }
  }, [pipelineDraft, scriptPipeConfig]);

  const handleImportPipeline = useCallback(() => {
    if (pipelineDraft && isPipelineExternal(pipelineDraft.pipelineType)) {
      setIsImportPipelineModalOpen(true);
    }
  }, [pipelineDraft]);

  const handleExportViaPipeline = useCallback(() => {
    if (pipelineDraft?.id && !isPipelineExternal(pipelineDraft.pipelineType)) {
      downloadViaPipeline({ pipelineId: pipelineDraft.id });
    }
  }, [pipelineDraft, downloadViaPipeline]);

  const handleDownloadPipeline = useCallback(() => {
    if (pipelineDraft?.id) {
      downloadPipeline({ pipelineId: pipelineDraft.id });
    }
  }, [pipelineDraft, downloadPipeline]);

  const handleDeletePipeline = useCallback(() => {
    setIsDeletePipelineModalOpen(true);
  }, [pipelineDraft]);

  useEffect(() => {
    if (parameterData) {
      setParameters(parameterData);
    }
  }, [isParameterDataSuccess, parameterData]);

  useEffect(() => {
    // selectedProject can load async
    if (projectData) {
      setSelectedProject(projectData);
    }
  }, [isProjectDataSuccess]);

  useEffect(() => {
    const updateWidth = () => {
      if (measureRef?.current) {
        const measuredWidth = measureRef.current?.offsetWidth;
        const newWidth = Math.max(100, Math.min(measuredWidth + 30, 480)); // min 100px, max-w-120
        setPipelineNameInputWidth(newWidth);
      }
    };

    updateWidth();
  }, [pipelineName, measureRef?.current]);

  const resetPipelineDatum = () => {
    setMainPipeline(null);
    setSelectedPipeline(null);
    setScriptPipeConfig([]);
    setPipelineDraft(null);
  };

  const handlePipelineNameUpdate = useCallback((value: string) => {
    increaseChangeCount();
    setPipelineName(value);
    setPipelineDraft(prevData => ({
      ...prevData,
      name: value,
    }));
  }, []);

  const handlePipelineRevisionChange = selected => {
    // Do not reroute / fetch if the user selects the current revision from the dropdown
    if (selected?.value && selected.value.toString() !== pipelineId) {
      const newPath = pathname.replace(pipelineId, selected.value);
      const shouldNavigate = navigate(newPath, () => {
        resetPipelineDatum();
        setSelectedRevision(selected);
        resetChangeCount();
      });

      if (!shouldNavigate) {
        setSelectedRevision((prev: any) => ({ ...prev }));
        return;
      }
    }
  };

  const handleRunAction = (action: TableContextMenuInterface) => {
    switch (action.name) {
      case "Create New Run":
        setIsCreateRunModalOpen(true);
        break;
      case "Existing Runs":
        setIsExistingRunsModalOpen(true);
        break;
      default:
        break;
    }
  };

  const handleContextMenuAction = (action: TableContextMenuInterface) => {
    switch (action.name) {
      case "Duplicate Pipeline":
        handleDuplicatePipeline();
        break;
      case "Create Revision":
        handleRevisePipeline();
        break;
      case "Delete Revision":
        handleDeletePipeline();
        break;
      case "Download Pipeline":
        handleDownloadPipeline();
        break;
      case "Import Pipeline":
        handleImportPipeline();
        break;
      case "Export Pipeline":
        setIsExportPipelineModalOpen(true);
        break;
      case "Download VF File":
        handleExportViaPipeline();
        break;
      default:
        break;
    }
  };

  const renderWarningSection = useCallback(
    (title: string, labels: string[] | undefined) => {
      if (!labels || labels.length === 0) return null;

      return (
        <div className="my-2">
          <h2 className="font-semibold">{title}</h2>
          <div className="flex gap-2 flex-wrap mt-2">
            {labels.map((label: string) => (
              <Badge
                key={label}
                text={label}
                textColor="text-yellow-800"
                fillColor="fill-yellow-500"
              />
            ))}
          </div>
        </div>
      );
    },
    []
  );

  const duplicateLabelsMessages = useMemo(() => {
    return (
      <div className="m-2 max-h-[200px] overflow-y-auto">
        <p className="my-2">
          Before saving, creating a new revision, or duplicating this pipeline,
          please ensure all labels are unique.
        </p>
        {renderWarningSection("Module Warnings", duplicatedLabels?.moduleNode)}
        {renderWarningSection("Model Warnings", duplicatedLabels?.modelNode)}
        {renderWarningSection("Skill Warnings", duplicatedLabels?.skillNode)}
        {renderWarningSection(
          "Process Warnings",
          duplicatedLabels?.processNode
        )}
      </div>
    );
  }, [duplicatedLabels, renderWarningSection]);

  const contextMenuOptions = useMemo(() => {
    let options = [
      {
        name: "Duplicate Pipeline",
        href: "#",
        disabled: isDuplicatedLabelExists,
        id: "duplicate-pipeline",
      },
    ];
    if (pipelineDraft?.isEditingAllowed) {
      options = [
        ...options,
        {
          name: "Create Revision",
          href: "#",
          disabled: isDuplicatedLabelExists,
          id: "create-revision",
        },
      ];
    }
    if (pipelineDraft?.isOwner) {
      options = [
        ...options,
        {
          name: "Delete Revision",
          href: "#",
          disabled: false,
          id: "delete-pipeline",
        },
      ];
    }
    options = [
      ...options,
      {
        name: "Download Pipeline",
        href: "#",
        disabled: false,
        id: "download-pipeline",
      },
    ];
    if (
      user?.userRole === USER_ROLES.ADMIN &&
      !isPipelineExternal(pipelineDraft?.pipelineType ?? null)
    ) {
      options = [
        ...options,
        {
          name: "Download VF File",
          href: "#",
          disabled: isExportingFoundry,
          id: "export-via-pipeline",
        },
      ];
    }
    options = [
      ...options,
      {
        name: "Export Pipeline",
        href: "#",
        disabled: false,
        id: "export-pipeline",
      },
    ];
    if (isPipelineExternal(pipelineDraft?.pipelineType ?? null)) {
      options = [
        ...options,
        {
          name: "Import Pipeline",
          href: "#",
          disabled: false,
          id: "import-pipeline",
        },
      ];
    }

    return options;
  }, [
    pipelineDraft?.isEditingAllowed,
    user,
    duplicatedLabels,
    isExportingFoundry,
  ]);

  return (
    <main className="via-content-wrapper">
      <div className="via-page-heading-wrapper">
        {isPipelineDataLoading ? (
          <TitleLoader />
        ) : (
          <div className="flex items-center">
            <span
              ref={measureRef}
              className="invisible fixed -left-[9999px] text-2xl font-bold md:text-3xl"
              aria-hidden="true">
              {pipelineName}
            </span>
            <input
              ref={pipelineNameInputRef}
              type="text"
              className={classNames(
                "mr-1 max-w-120 border-none text-2xl font-bold leading-10 text-gray-900 disabled:text-gray-900 sm:truncate sm:tracking-tight md:text-3xl md:leading-normal",
                pipelineDraft?.isEditingAllowed
                  ? "hover:bg-gray-100 ease-in-out duration-300"
                  : ""
              )}
              style={{
                width: `${pipelineNameInputWidth}px`,
                transition: "width 0.2s ease",
              }}
              disabled={!pipelineDraft?.isEditingAllowed}
              placeholder="Enter Pipeline Name"
              onClick={() => setIsEditingPipelineName(true)}
              onChange={e => handlePipelineNameUpdate(e.target.value)}
              onBlur={() => setIsEditingPipelineName(false)}
              value={pipelineName}
            />
            {pipelineDraft?.isEditingAllowed && !isEditingPipelineName && (
              <button
                data-testid="edit-pipeline-name"
                onClick={() => {
                  pipelineNameInputRef?.current?.focus();
                  setIsEditingPipelineName(true);
                }}
                className="rounded-md bg-white p-1 text-gray-500 hover:bg-gray-50 hover:text-gray-600"
                title="Edit pipeline name">
                <PencilIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        {pipelineDraft && selectedRevision && (
          <div className="flex items-center space-x-2">
            <div className="w-50 rounded-md border-gray-300 bg-white text-gray-500">
              <SelectComponent
                name="pipelineRevisions"
                options={pipelineRevisions}
                isSearchable={false}
                enableClear={false}
                isLoading={
                  isPipelineRevisionHistoryLoading || isPipelineDataLoading
                }
                value={selectedRevision}
                onChange={handlePipelineRevisionChange}
                formatOptionLabel={(option, { context }) => {
                  return context === "menu" ? (
                    <div className="flex flex-col items-start">
                      <span>
                        {option?.version} on{" "}
                        {formatDateString(option?.createdAt ?? "", "LL")}
                      </span>
                      <span className="line-clamp-2 text-xs text-gray-400">
                        {option?.revComment}
                      </span>
                    </div>
                  ) : (
                    option?.label
                  );
                }}
              />
            </div>
            {pipelineDraft?.isEditingAllowed && (
              <button
                id="save-pipeline"
                className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 items-center gap-1"
                onClick={handleSavePipeline}
                disabled={isDuplicatedLabelExists}>
                <SaveIcon /> Save
              </button>
            )}
            <ContextButton
              text={BUTTONS.RUN}
              options={[
                { name: "Create New Run", href: "#" },
                { name: "Existing Runs", href: "#" },
              ]}
              onSelect={handleRunAction}
              className="h-[40px] inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 items-center gap-1"
            />
            <TableContextMenu
              options={contextMenuOptions}
              onSelect={handleContextMenuAction}
              className="rounded-md border border-indigo-600 px-2 py-2 text-sm font-semibold text-indigo-600 shadow-sm hover:border-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            />
          </div>
        )}
      </div>
      {isPipelineDataLoading ? (
        <div className="via-loading-spinner">
          <LogoSpinner />
        </div>
      ) : isError ? (
        <div className=" via-generic-error h-[78dvh] justify-center">
          <GenericError
            message={ERROR_YOU_ARE_NOT_AUTHORIZED_TO_ACCESS_THIS_PIPELINE}
            displaySupportTeam={false}
          />
        </div>
      ) : pipelineDraft ? (
        <>
          {isDuplicatedLabelExists && (
            <MessageBanner
              messages={duplicateLabelsMessages}
              type={BannerType.Warning}
              title="Warning: Duplicate Labels Detected"
            />
          )}
          {/* Only external pipelines have files to analyse, so for a via-native one the
              request is guaranteed to return no findings, and for a guest viewing a public
              pipeline it returns a 4xx because the lint route has no allowGuestAccess. */}
          {isPipelineExternal(pipelineDraft.pipelineType) && (
            <PipelineLintPanel pipelineId={pipelineId} />
          )}
          <SocketProvider socketUri={SOCKET_URI}>
            <HorizontalTabs
              hidden={hidden}
              buttonIdPrefix="pipeline"
              options={PIPELINE_TABS}
            />
            <Outlet />
          </SocketProvider>
        </>
      ) : (
        <></>
      )}
      {isDeletePipelineModalOpen && (
        <DeletePipelineModal
          open={isDeletePipelineModalOpen}
          setOpen={setIsDeletePipelineModalOpen}
          pipelineName={pipelineDraft?.name}
          pipelineId={pipelineDraft?.id}
          pipelineVersion={pipelineDraft?.version}
        />
      )}
      {isImportPipelineModalOpen && (
        <ImportPipelineModal
          open={isImportPipelineModalOpen}
          setOpen={setIsImportPipelineModalOpen}
          pipelineDraft={pipelineDraft ?? undefined}
        />
      )}
      {isExportPipelineModalOpen && pipelineDraft?.id && (
        <ExportPipelineModal
          open={isExportPipelineModalOpen}
          setOpen={setIsExportPipelineModalOpen}
          onDownloadVfFile={
            user?.userRole === USER_ROLES.ADMIN &&
            !isPipelineExternal(pipelineDraft.pipelineType ?? null)
              ? handleExportViaPipeline
              : undefined
          }
        />
      )}
      {isSavePipelineModalOpen && (
        <SavePipelineModal
          open={isSavePipelineModalOpen}
          setOpen={setIsSavePipelineModalOpen}
          draft={pipelineDraft}
          scripts={scriptPipeConfig}
          userRole={user?.userRole}
          handleGoToCreateRevision={() => {
            setIsSavePipelineModalOpen(false);
            setIsRevisePipelineModalOpen(true);
          }}
        />
      )}
      {isRevisePipelineModalOpen && (
        <RevisePipelineModal
          open={isRevisePipelineModalOpen}
          setOpen={setIsRevisePipelineModalOpen}
          draft={pipelineDraft}
          scripts={scriptPipeConfig}
        />
      )}
      {isDuplicatePipelineModalOpen && (
        <DuplicatePipelineModal
          open={isDuplicatePipelineModalOpen}
          setOpen={setIsDuplicatePipelineModalOpen}
          draft={pipelineDraft}
          scripts={scriptPipeConfig}
        />
      )}
      {isCreateRunModalOpen && (
        <CreateRunModal
          open={isCreateRunModalOpen}
          setOpen={setIsCreateRunModalOpen}
          projectId={projectId}
          pipelineId={mainPipeline.id}
        />
      )}
      {isExistingRunsModalOpen && (
        <ExistingRunsModal
          open={isExistingRunsModalOpen}
          setOpen={setIsExistingRunsModalOpen}
          pipelineId={mainPipeline.id}
        />
      )}
      {NavigationBlockerModal}
    </main>
  );
}
